'use client';

import type { EPlatform } from '@/utils/enums/platform-enum';
import { serializeKey } from '@/utils/swr/keys';
import { useCallback, useMemo, useRef } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import {
  buildStyleHotProductViewStateKey,
  createStyleHotProductViewState,
  type StyleHotProductViewState,
} from './styleHotProductViewState';

interface UseStyleHotProductViewStateParams {
  marketingPageId?: number;
  isPreview: boolean;
  adp?: string;
  deviceType: EPlatform;
  defaultActiveTabIndex: number;
}

export function useStyleHotProductViewState({
  marketingPageId,
  isPreview,
  adp,
  deviceType,
  defaultActiveTabIndex,
}: UseStyleHotProductViewStateParams) {
  const key = useMemo(
    () =>
      serializeKey(
        buildStyleHotProductViewStateKey({ marketingPageId, isPreview, adp, deviceType }),
      ),
    [adp, deviceType, isPreview, marketingPageId],
  );
  const fallbackData = useMemo(
    () => createStyleHotProductViewState(defaultActiveTabIndex),
    [defaultActiveTabIndex],
  );
  const { cache } = useSWRConfig();
  const restoredFromCacheRef = useRef(cache.get(key)?.data !== undefined);
  const { data, mutate } = useSWR<StyleHotProductViewState>(key, null, {
    fallbackData,
    revalidateOnMount: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
  });

  const updateViewState = useCallback(
    (patch: Partial<StyleHotProductViewState>) => {
      return mutate(
        (current) => ({
          ...fallbackData,
          ...current,
          ...patch,
        }),
        { revalidate: false },
      );
    },
    [fallbackData, mutate],
  );

  return {
    viewState: data || fallbackData,
    updateViewState,
    restoredFromCache: restoredFromCacheRef.current,
  };
}
